import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  SquaresFour, Robot, PlusSquare, Lightning, Storefront, Books, CurrencyEur, GearSix, SignOut, List, HardDrives, Package, Crown, UsersThree,
} from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/layout/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Panel", icon: SquaresFour, id: "dashboard" },
  { to: "/agents", label: "Mis agentes", icon: Robot, id: "agents", end: true },
  { to: "/agents/new", label: "Crear agente", icon: PlusSquare, id: "create" },
  { to: "/skills", label: "Skills", icon: Lightning, id: "skills" },
  { to: "/library", label: "Biblioteca", icon: Books, id: "library" },
  { to: "/sales", label: "Ventas", icon: CurrencyEur, id: "sales" },
  { to: "/marketplace", label: "Marketplace", icon: Storefront, id: "marketplace" },
  { to: "/models", label: "Modelos locales", icon: HardDrives, id: "models" },
  { to: "/software", label: "Software", icon: Package, id: "software" },
  { to: "/settings", label: "Ajustes", icon: GearSix, id: "settings" },
];

const ADMIN_NAV = [
  { to: "/admin/products", label: "Mi catálogo", icon: Crown, id: "admin-products" },
  { to: "/admin/center", label: "Clientes y audiencia", icon: UsersThree, id: "admin-center" },
];

const SidebarBody = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const items = user?.is_admin ? [...NAV, ...ADMIN_NAV] : NAV;
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b border-white/10 px-5"><Logo to="/dashboard" /></div>
      <nav className="flex-1 space-y-px overflow-y-auto p-3">
        {items.map(({ to, label, icon: I, id, end }) => (
          <NavLink
            key={to} to={to} end={end} onClick={onNavigate} data-testid={`sidebar-${id}-link`}
            className={({ isActive }) =>
              `group flex items-center gap-3 border-l-2 px-3 py-2.5 text-sm font-medium ${isActive ? "border-blue-500 bg-white/5 text-white" : "border-transparent text-zinc-400 hover:bg-white/[0.03] hover:text-white"}`
            }
            style={{ transition: "background-color 160ms ease-out, color 160ms ease-out" }}
          >
            <I size={18} weight="duotone" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="flex items-center gap-3">
          {user?.picture ? <img src={user.picture} alt="" className="h-9 w-9 object-cover" /> : <div className="grid h-9 w-9 place-items-center bg-blue-600 text-sm font-bold">{user?.name?.[0]}</div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="truncate text-xs text-zinc-500">{user?.email}</p>
          </div>
          <button onClick={logout} className="p-2 text-zinc-500 hover:text-white" title="Cerrar sesión" data-testid="logout-button">
            <SignOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export const AppShell = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="noise min-h-screen bg-[#050505] text-white">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-white/10 bg-[#080808] lg:block" data-testid="app-sidebar">
        <SidebarBody />
      </aside>
      <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-white/10 bg-black/60 px-4 backdrop-blur-xl lg:hidden">
        <Logo to="/dashboard" />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="p-2" data-testid="mobile-menu-button"><List size={22} /></button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 border-white/10 bg-[#080808] p-0 text-white">
            <SidebarBody onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>
      <main className="lg:pl-64">
        <Outlet />
      </main>
    </div>
  );
};

export const PageHeader = ({ eyebrow, title, subtitle, actions }) => (
  <div className="flex flex-col gap-6 border-b border-white/10 px-6 py-10 md:flex-row md:items-end md:justify-between lg:px-12">
    <div className="reveal">
      {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
      <h1 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl lg:text-5xl">{title}</h1>
      {subtitle && <p className="mt-3 max-w-2xl text-base text-zinc-400">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
  </div>
);
